import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, LabelValueCard } from 'components/Card';
import { useChain, usePortfolio } from 'contexts';
import { formatAccount } from 'utils/chain';
import { formatCount, formatUsd } from 'utils/number';
import { Currency } from 'utils/types';

const PortfolioCards = () => {
  const { t } = useTranslation();
  const { styleSlug } = useChain();
  const {
    accounts,
    walletBalance,
    walletBalanceUsd,
    walletBalanceTotalUsd,
    lockedBalance,
    lockedBalanceUsd,
    lockedBalanceTotalUsd,
    unclaimedBalance,
    unclaimedBalanceUsd,
    unclaimedBalanceTotalUsd,
    netWorthBalanceTotalUsd,
  } = usePortfolio();

  const sortedWalletCurrencies = (Object.keys(walletBalance) as Array<Currency>)
    .filter((currency) => walletBalance[currency])
    .sort((a, b) => walletBalanceUsd[b]! - walletBalanceUsd[a]!);
  const sortedLockedCurrencies = (Object.keys(lockedBalance) as Array<Currency>)
    .filter((currency) => lockedBalance[currency])
    .sort((a, b) => lockedBalanceUsd[b]! - lockedBalanceUsd[a]!);
  const sortedUnclaimedCurrencies = (Object.keys(unclaimedBalance) as Array<Currency>)
    .filter((currency) => unclaimedBalance[currency])
    .sort((a, b) => unclaimedBalanceUsd[b]! - unclaimedBalanceUsd[a]!);

  return (
    <>
      <Card
        className="mb-2 md:mb-4"
        title={t('portfolio.net_worth')}
        body={formatUsd(netWorthBalanceTotalUsd)}
        footer={accounts.map(formatAccount).join(' | ')}
        filled
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
        <LabelValueCard label={t('portfolio.wallet')} value={formatUsd(walletBalanceTotalUsd)}>
          {walletBalanceTotalUsd > 0 && (
            <table className="w-full rounded-b-10 table-fixed text-left bg-white dark:bg-gray-111">
              <thead
                className={`uppercase bg-${styleSlug}-lighter dark:bg-${styleSlug}-primary text-${styleSlug}-dark dark:text-${styleSlug}-light text-xs`}
              >
                <tr>
                  <th className="pl-4 py-1">{t('portfolio.tokens')}</th>
                  <th className="pr-4 py-1 text-right">{t('portfolio.value')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedWalletCurrencies.map((currency, i) => (
                  <tr
                    key={currency}
                    className={
                      i !== sortedWalletCurrencies.length - 1
                        ? 'border-b border-gray-e6e6e6 dark:border-gray-444'
                        : ''
                    }
                  >
                    <td className="pl-4 py-2">
                      <span className="dark:text-white">
                        {formatCount(walletBalance[currency]!, 2)}
                      </span>{' '}
                      <span className="text-gray-999">{currency}</span>
                    </td>
                    <td className="pr-4 py-2 text-right dark:text-white">
                      {formatUsd(walletBalanceUsd[currency]!)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </LabelValueCard>
        <LabelValueCard label={t('portfolio.staked')} value={formatUsd(lockedBalanceTotalUsd)}>
          {lockedBalanceTotalUsd > 0 && (
            <table className="w-full rounded-b-10 table-fixed text-left bg-white dark:bg-gray-111">
              <thead
                className={`uppercase bg-${styleSlug}-lighter dark:bg-${styleSlug}-primary text-${styleSlug}-dark dark:text-${styleSlug}-light text-xs`}
              >
                <tr>
                  <th className="pl-4 py-1">{t('portfolio.tokens')}</th>
                  <th className="pr-4 py-1 text-right">{t('portfolio.value')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedLockedCurrencies.map((currency, i) => (
                  <tr
                    key={currency}
                    className={
                      i !== sortedLockedCurrencies.length - 1
                        ? 'border-b border-gray-e6e6e6 dark:border-gray-444'
                        : ''
                    }
                  >
                    <td className="pl-4 py-2">
                      <span className="dark:text-white">
                        {formatCount(lockedBalance[currency]!, 2)}
                      </span>{' '}
                      <span className="text-gray-999">{currency}</span>
                    </td>
                    <td className="pr-4 py-2 text-right dark:text-white">
                      {formatUsd(lockedBalanceUsd[currency]!)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </LabelValueCard>
        <LabelValueCard
          label={t('portfolio.unclaimed')}
          value={formatUsd(unclaimedBalanceTotalUsd)}
        >
          {unclaimedBalanceTotalUsd > 0 && (
            <table className="w-full rounded-b-10 table-fixed text-left bg-white dark:bg-gray-111">
              <thead
                className={`uppercase bg-${styleSlug}-lighter dark:bg-${styleSlug}-primary text-${styleSlug}-dark dark:text-${styleSlug}-light text-xs`}
              >
                <tr>
                  <th className="pl-4 py-1">{t('portfolio.tokens')}</th>
                  <th className="pr-4 py-1 text-right">{t('portfolio.value')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedUnclaimedCurrencies.map((currency, i) => (
                  <tr
                    key={currency}
                    className={
                      i !== sortedUnclaimedCurrencies.length - 1
                        ? 'border-b border-gray-e6e6e6 dark:border-gray-444'
                        : ''
                    }
                  >
                    <td className="pl-4 py-2">
                      <span className="dark:text-white">
                        {formatCount(unclaimedBalance[currency]!, 2)}
                      </span>{' '}
                      <span className="text-gray-999">{currency}</span>
                    </td>
                    <td className="pr-4 py-2 text-right dark:text-white">
                      {formatUsd(unclaimedBalanceUsd[currency]!)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </LabelValueCard>
      </div>
    </>
  );
};

export default PortfolioCards;
